import { useMemo, useState, useCallback } from "react";
import { BlockStack, Button, Link, Text, DataTable } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import UploadFile from "features/upload-file/upload-file";
import { API_URL } from "../../constants";
import { Customer } from "types/Customer";

interface VatCheckSectionProps {
  companyId?: string;
  customer: Customer;
  vatCheckEntries: any[];
  vatCheckDocument?: any;
  isQuestionnaireCompleted: boolean;
  areRequiredSanctionsCompleted: () => boolean;
  onPerformVatCheck: () => void | Promise<void>;
  isPerformingVatCheck: boolean;
  canPerformVatCheck: boolean;
  year?: number;
  vatCheckFileCategory?: string | null;
  vatCheckFileType?: string | null;
  customerFinance?: string | null;
}

const VatCheckSection = ({
  companyId,
  customer,
  vatCheckEntries,
  vatCheckDocument,
  isQuestionnaireCompleted,
  areRequiredSanctionsCompleted,
  onPerformVatCheck,
  isPerformingVatCheck,
  canPerformVatCheck,
  year,
  vatCheckFileCategory,
  vatCheckFileType,
  customerFinance,
}: VatCheckSectionProps) => {
  const { t } = useTranslation();
  const [isManualUploadVisible, setIsManualUploadVisible] = useState(false);

  const previousStepCompleted =
    isQuestionnaireCompleted && areRequiredSanctionsCompleted();

  const handleManualVatUploadClick = useCallback(() => {
    window.open(
      "https://ec.europa.eu/taxation_customs/vies/#/vat-validation",
      "_blank",
      "noopener,noreferrer"
    );
    setIsManualUploadVisible(true);
  }, []);

  const vatCheckHistoryRows = useMemo(
    () =>
      vatCheckEntries.map((entry) => {
        const statusText =
          entry?.isValid === true
            ? t("vat_check_confirmed")
            : entry?.isValid === false
            ? t("vat_check_invalid")
            : t("pending");
        const timestamp =
          entry?.createdAt ||
          entry?.created_at ||
          entry?.updatedAt ||
          entry?.updated_at ||
          entry?.created ||
          entry?.updated ||
          null;
        const formattedTimestamp = (() => {
          if (!timestamp) {
            return "-";
          }
          const parsed = new Date(timestamp);
          return Number.isNaN(parsed.getTime())
            ? timestamp
            : parsed.toLocaleString();
        })();
        const fileNode = entry?.file ? (
          <Link
            url={`${API_URL}/${companyId}/files/download/${entry.file.id}`}
            target="_blank"
          >
            {entry.file.name || entry.file.title || t("download_file")}
          </Link>
        ) : (
          <Text as="span" tone="subdued">
            {t("no_file_uploaded") || "-"}
          </Text>
        );
        return [statusText, formattedTimestamp, fileNode];
      }),
    [vatCheckEntries, companyId, t]
  );

  const vatHistoryTable =
    vatCheckHistoryRows.length > 0 ? (
      <DataTable
        columnContentTypes={["text", "text", "text"]}
        headings={[
          t("status") || "Status",
          t("datetime") || "Date & time",
          t("document") || "Document",
        ]}
        rows={vatCheckHistoryRows}
      />
    ) : null;

  if (!previousStepCompleted) {
    return (
      <Text as="p" tone="subdued">
        {t("complete_previous_step")}
      </Text>
    );
  }

  return (
    <BlockStack gap="200">
      {vatHistoryTable}
      <Text as="p" tone="subdued">
        {t("vat_check_description")}
      </Text>

      <Button
        onClick={onPerformVatCheck}
        loading={isPerformingVatCheck}
        disabled={!canPerformVatCheck}
      >
        {t("perform_vat_check")}
      </Button>

      {!isManualUploadVisible && !vatCheckDocument && (
        <Button onClick={handleManualVatUploadClick} variant="secondary">
          {t("vat_check_manual")}
        </Button>
      )}

      {(isManualUploadVisible || vatCheckDocument) && (
        <BlockStack gap="200">
          {vatCheckDocument ? (
            <Link
              url={`${API_URL}/${companyId}/files/download/${vatCheckDocument.id}`}
              target="_blank"
            >
              {vatCheckDocument.name ||
                vatCheckDocument.title ||
                t("download_file")}
            </Link>
          ) : (
            <>
              <Text as="p" tone="subdued">
                {t("vat_check_upload_description")}
              </Text>
              <UploadFile
                onChange={() => {
                  setIsManualUploadVisible(true);
                }}
                label={t("vat_check_manual_upload")}
                subject="customer"
                subjectId={customer.id}
                categoryType="internal"
                isVatCheckDocument={true}
                year={year}
                fileCategory={vatCheckFileCategory}
                fileType={vatCheckFileType}
                customerFinance={customerFinance}
              />
            </>
          )}
        </BlockStack>
      )}
    </BlockStack>
  );
};

export default VatCheckSection;
